import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Container, Grid, Paper, Skeleton, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import Sidebar from "../Sidebar/Sidebar";
import useAuth from "../../hooks/useAuth";

import { getAnonymizationRunStats } from "../../services/anonymizationRunsService";
import { listTenants } from "../../services/tenantSuperadminService";

import AdminBillingFilters from "./AdminBillingFilters";
import AdminBillingTotals from "./AdminBillingTotals";
import AdminBillingBreakdown from "./AdminBillingBreakdown";

const todayYMD = () => new Date().toISOString().slice(0, 10);

const firstDayOfMonthYMD = () => {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().slice(0, 10);
};

export default function AdminBillingPage() {
  const { isLoggedIn, user, isLoading } = useAuth();
  const { t } = useTranslation();

  const isAdmin = Boolean(user?.is_superuser || user?.is_staff);

  const [tenants, setTenants] = useState([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);

  const [filters, setFilters] = useState({
    from: firstDayOfMonthYMD(),
    to: todayYMD(),
    tenant_id: "",
    process_type: "",
    status: "",
  });

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const tenantChosen = Boolean(filters?.tenant_id);

  const queryParams = useMemo(
    () => ({
      tenantId: filters?.tenant_id || null,
      createdAtGte: filters?.from || null,
      createdAtLte: filters?.to || null,
      processType: filters?.process_type || null,
      status: filters?.status || null,
    }),
    [filters]
  );

  const loadTenants = async () => {
    setTenantsLoading(true);
    try {
      const res = await listTenants();
      const list = Array.isArray(res?.results) ? res.results : Array.isArray(res) ? res : [];
      setTenants(list);
    } catch {
      setTenants([]);
    } finally {
      setTenantsLoading(false);
    }
  };

  const fetchData = async () => {
    setError("");

    // ✅ endpoint requires tenant_id for superadmin
    if (!queryParams.tenantId) {
      setData(null);
      return;
    }

    setLoading(true);
    try {
      const res = await getAnonymizationRunStats(queryParams);
      setData(res);
    } catch (e) {
      setData(null);
      setError(e?.message || "Failed to load billing data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isLoggedIn || isLoading || !isAdmin) return;
    loadTenants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, isLoading, isAdmin]);

  // Optional: auto-refresh when tenant changes
  useEffect(() => {
    if (!isLoggedIn || isLoading || !isAdmin) return;
    if (!tenantChosen) return;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantChosen, filters?.tenant_id]);

  const renderSkeleton = () => (
    <>
      <Sidebar user={user} />
      <Container maxWidth="xl">
        <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
          <Skeleton variant="text" width={280} height={40} />
          <Skeleton variant="text" width={520} height={28} />
          <Skeleton variant="rectangular" height={260} sx={{ mt: 2 }} />
        </Paper>
      </Container>
    </>
  );

  if (!isLoggedIn || isLoading) return renderSkeleton();

  if (!isAdmin) {
    return (
      <>
        <Sidebar user={user} />
        <Container maxWidth="xl">
          <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
            <Typography variant="h4" gutterBottom>
              {t("adminBilling.pageTitle", "Admin billing")}
            </Typography>
            <Alert severity="warning">
              {t("adminBilling.notAllowed", "You do not have permission to view billing analytics.")}
            </Alert>
          </Paper>
        </Container>
      </>
    );
  }

  return (
    <>
      <Sidebar user={user} />
      <Container maxWidth="xl">
        <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h4" gutterBottom>
                {t("adminBilling.pageTitle", "Štatistika")}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                {t(
                  "adminBilling.pageSubtitle",
                  "Prehľad využívania. Vyberte organizáciu a časové obdobie."
                )}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <AdminBillingFilters
                filters={filters}
                setFilters={setFilters}
                loading={loading}
                tenants={tenants}
                tenantsLoading={tenantsLoading}
                onRefresh={fetchData}
                getDefaultFrom={firstDayOfMonthYMD}
                getDefaultTo={todayYMD}
              />
            </Grid>

            {!tenantChosen ? (
              <Grid item xs={12}>
                <Alert severity="info">
                  {t("adminBilling.selectTenantFirst", "Najprv vyberte organizáciu pre zobrazenie štatistík.")}
                </Alert>
              </Grid>
            ) : (
              <>
                {error ? (
                  <Grid item xs={12}>
                    <Alert severity="error">{error}</Alert>
                  </Grid>
                ) : null}

                <Grid item xs={12}>
                  <AdminBillingTotals totals={data?.totals || null} loading={loading} />
                </Grid>

                <Grid item xs={12}>
                  <AdminBillingBreakdown data={data} loading={loading} />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ color: "text.secondary" }}>
                    <Typography variant="caption">
                      {t(
                        "adminBilling.tip",
                        "Tip: Toto sú štatistiky z behov (runs). Ak chceš billing podľa eventov, použi UsageEvent."
                      )}
                    </Typography>
                  </Box>
                </Grid>
              </>
            )}
          </Grid>
        </Paper>
      </Container>
    </>
  );
}